import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { errorText } from '@/lib/errors';

type Toast = { id: number; text: string; tone: 'ok' | 'warn' | 'danger' | 'info'; action?: { label: string; onClick: () => void }; until?: number };
type Ctx = { show: (text: string, tone?: Toast['tone'], action?: Toast['action'], ms?: number) => void; error: (e: unknown) => void; dismiss: (id: number) => void };
const ToastCtx = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(1);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback((text: string, tone: Toast['tone'] = 'info', action?: Toast['action'], ms = 5000) => {
    const id = seq.current++;
    setItems((xs) => [...xs, { id, text, tone, action }]);
    if (ms > 0) setTimeout(() => dismiss(id), ms);
  }, [dismiss]);
  const error = useCallback((e: unknown) => show(errorText(e), 'danger', undefined, 8000), [show]);
  const value = useMemo(() => ({ show, error, dismiss }), [show, error, dismiss]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast tone-${t.tone}`}>
            <span>{t.text}</span>
            {t.action && <button type="button" className="btn btn-sm" onClick={() => { t.action!.onClick(); dismiss(t.id); }}>{t.action.label}</button>}
            <button type="button" className="toast-x" aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export function useToast() {
  const c = useContext(ToastCtx);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c;
}
